import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { SignaturePad } from './BrokerInboxExtras';
import { useAuth } from '../contexts/AuthContext';
import {
  Loader2, ScanSearch, AlertTriangle, Info, CheckCircle2, RefreshCw, Upload, FileDown, Copy, Mail, PenLine,
  ChevronDown, ChevronUp, Circle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

const buildEmailBody = (flags, recipientName) => {
  const greet = recipientName ? `Guten Tag ${recipientName},` : 'Guten Tag,';
  const items = flags.map((f, i) => `${i + 1}. ${f.title}${f.clause ? ` (${f.clause})` : ''}\n${f.comment || f.why}`);
  return `${greet}\n\nvielen Dank für die Übersendung des NDA. Nach interner Prüfung möchten wir folgende Anmerkungen mitgeben:\n\n${items.join('\n\n')}\n\nWir freuen uns auf Ihre Rückmeldung und stehen für Rückfragen gern zur Verfügung.\n\nHerzliche Grüße`;
};

function NdaStageStepper({ nda, review }) {
  const signed = !!nda.signed_at;
  const signPending = !signed && !!nda.sign_request;
  const commented = !!nda.remarks_sent_at;
  const reviewDone = nda.review_status === 'done';
  const reviewRunning = nda.review_status === 'running';
  const steps = [
    { key: 'received', label: 'NDA erhalten', at: nda.uploaded_at, done: true },
    { key: 'review', label: reviewRunning ? 'In Prüfung…' : 'Prüfung', at: review?.analyzed_at, done: reviewDone, active: reviewRunning },
    { key: 'comments', label: 'Kommentare versendet', at: nda.remarks_sent_at, done: commented },
    { key: 'signed', label: signPending ? 'Freigabe läuft…' : 'Unterschrieben', at: nda.signed_at, done: signed, active: signPending },
  ];
  const currentIdx = signed || signPending ? 3 : commented ? 2 : 1;
  return (
    <div className="flex items-center flex-wrap gap-y-1.5" data-testid="broker-nda-stage-stepper">
      {steps.map((s, i) => {
        const cls = s.done
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
          : s.active
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
            : i === currentIdx
              ? 'border-sky-500/40 bg-sky-500/10 text-sky-300'
              : 'border-border bg-secondary/30 text-muted-foreground';
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <div className={`h-px w-4 mx-1 shrink-0 ${i <= currentIdx ? 'bg-emerald-500/40' : 'bg-border'}`} />}
            <div className={`flex items-center gap-1.5 rounded-sm border px-2 py-1 ${cls}`} data-testid={`broker-nda-stage-${s.key}`}>
              {s.done ? <CheckCircle2 className="w-3 h-3 shrink-0" />
                : s.active ? <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                : <Circle className="w-3 h-3 shrink-0 opacity-50" />}
              <span className="text-[10px] font-mono leading-tight">
                {s.label}
                {s.at && <span className="block text-[9px] opacity-70">{fmtDt(s.at)}</span>}
              </span>
            </div>
          </React.Fragment>
        );
      })}
      {commented && !signed && !signPending && (
        <span className="text-[10px] text-muted-foreground ml-2" data-testid="broker-nda-stage-hint">
          Feedback liegt beim Broker
        </span>
      )}
    </div>
  );
}

export function BrokerNdaReviewCard({ projectId }) {
  const { user } = useAuth();
  const [nda, setNda] = useState(null);
  const [versions, setVersions] = useState([]);
  const [mailNda, setMailNda] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState({});
  const [mailOpen, setMailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [mail, setMail] = useState({ to: '', cc: '', subject: '', body: '' });
  const [signOpen, setSignOpen] = useState(false);
  const [sigMode, setSigMode] = useState('draw');
  const [sigName, setSigName] = useState('');
  const [sigData, setSigData] = useState('');
  const [signSending, setSignSending] = useState(false);
  const [signMail, setSignMail] = useState({ to: '', cc: '', body: '' });
  const [showReview, setShowReview] = useState(false);
  const fileRef = useRef(null);

  const initChecked = (review) => {
    const init = {};
    (review?.red_flags || []).forEach((_, i) => { init[i] = true; });
    setChecked(init);
  };

  const load = useCallback(() => {
    api.getBrokerNda(projectId)
      .then(r => {
        setNda(r.data.nda);
        setVersions(r.data.versions || []);
        setMailNda(r.data.mail_nda || null);
        setRecipients(r.data.suggested_recipients || []);
        initChecked(r.data.nda?.review);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // The AI check runs server-side in the background — poll while it's running.
  useEffect(() => {
    if (nda?.review_status !== 'running') return;
    const iv = setInterval(async () => {
      try {
        const r = await api.getBrokerNda(projectId);
        const fresh = r.data.nda;
        setRecipients(r.data.suggested_recipients || []);
        setVersions(r.data.versions || []);
        setNda(fresh);
        if (fresh?.review_status === 'done') {
          initChecked(fresh.review);
          toast.success('NDA-Analyse abgeschlossen');
        } else if (fresh?.review_status === 'error') {
          toast.error(fresh.review_error || 'Analyse fehlgeschlagen — bitte erneut versuchen');
        }
      } catch { /* transient — keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [nda?.review_status, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (name.endsWith('.doc')) { toast.error('Altes Word-Format (.doc) — bitte als .docx oder PDF speichern'); return; }
    if (!name.endsWith('.pdf') && !name.endsWith('.docx')) { toast.error('Bitte ein PDF oder Word-Dokument (.docx) hochladen'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.uploadBrokerNda(projectId, fd);
      setNda(r.data.nda);
      initChecked(r.data.nda?.review);
      toast.success('NDA hochgeladen — die KI-Analyse läuft im Hintergrund');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload/Analyse fehlgeschlagen');
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const importFromMail = async () => {
    setBusy(true);
    try {
      await api.importBrokerNdaFromMail(projectId);
      toast.success('NDA aus der Mail als neue Version übernommen — die KI-Analyse läuft im Hintergrund');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Übernahme fehlgeschlagen');
    }
    setBusy(false);
  };

  const activateVersion = async (vid) => {
    setBusy(true);
    try {
      await api.activateBrokerNdaVersion(projectId, vid);
      toast.success('NDA-Version gewechselt');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versionswechsel fehlgeschlagen');
    }
    setBusy(false);
  };

  const rerun = async () => {
    setBusy(true);
    try {
      const r = await api.rerunBrokerNda(projectId);
      setNda(r.data.nda);
      toast.success('Neue Analyse gestartet — läuft im Hintergrund');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analyse fehlgeschlagen');
    }
    setBusy(false);
  };

  const copyText = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  const review = nda?.review;
  const activeVersionIdx = versions.findIndex(v => v.active);
  const reds = review?.red_flags || [];
  const yellows = review?.yellow_flags || [];
  const selected = reds.filter((_, i) => checked[i]);
  const reviewCollapsed = !!(nda?.remarks_sent_at || nda?.signed_at || nda?.sign_request);
  const detailsVisible = !reviewCollapsed || showReview;

  const openMailDialog = () => {
    if (selected.length === 0) { toast.error('Keine Kommentare ausgewählt'); return; }
    const first = recipients[0];
    setMail({
      to: first?.email || '',
      cc: '',
      subject: `NDA — Anmerkungen (${nda?.filename || 'Entwurf'})`,
      body: buildEmailBody(selected, first?.name || ''),
    });
    setMailOpen(true);
  };

  const sendMail = async () => {
    const to = mail.to.split(/[,;]/).map(s => s.trim()).filter(Boolean);
    if (to.length === 0) { toast.error('Empfänger fehlt'); return; }
    setSending(true);
    try {
      await api.sendBrokerNdaEmail(projectId, {
        to, cc: mail.cc.split(/[,;]/).map(s => s.trim()).filter(Boolean),
        subject: mail.subject, body: mail.body,
      });
      toast.success('E-Mail an den Broker gesendet');
      setMailOpen(false);
      setShowReview(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Senden fehlgeschlagen');
    }
    setSending(false);
  };

  const isPdf = (nda?.file || '').toLowerCase().endsWith('.pdf');

  const openSignDialog = () => {
    const first = recipients[0];
    setSigMode('draw'); setSigData('');
    setSigName(user?.name || '');
    setSignMail({
      to: first?.email || '',
      cc: '',
      body: `Guten Tag${first?.name ? ` ${first.name}` : ''},\n\nvielen Dank für die Übersendung des NDA. Anbei erhalten Sie das von uns gegengezeichnete Exemplar.\n\nHerzliche Grüße`,
    });
    setSignOpen(true);
  };

  const requestSign = async () => {
    const to = signMail.to.split(/[,;]/).map(s => s.trim()).filter(Boolean);
    if (to.length === 0) { toast.error('Empfänger fehlt'); return; }
    if (sigMode === 'draw' && !sigData) { toast.error('Bitte Unterschrift zeichnen'); return; }
    if (sigMode === 'type' && sigName.trim().length < 3) { toast.error('Bitte vollständigen Namen angeben'); return; }
    setSignSending(true);
    try {
      await api.signBrokerNda(projectId, {
        to,
        cc: signMail.cc.split(/[,;]/).map(s => s.trim()).filter(Boolean),
        email_body: signMail.body,
        signature_name: sigName.trim(),
        signature_png_b64: (sigMode === 'draw' && sigData) ? sigData.split(',')[1] : '',
      });
      toast.success('Freigabe angefragt — ein anderes Teammitglied muss in den Pending Reviews freigeben');
      setSignOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anfrage fehlgeschlagen');
    }
    setSignSending(false);
  };

  const cancelSign = async () => {
    try {
      await api.cancelNdaApproval(projectId);
      toast.success('Freigabe-Anfrage zurückgezogen');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="broker-nda-review-card">
      <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" data-testid="broker-nda-file-input"
        onChange={e => upload(e.target.files?.[0])} />
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="font-heading text-base text-foreground flex items-center gap-2">
          <ScanSearch className="w-4 h-4 text-sky-400" /> Broker-NDA prüfen
        </p>
        <div className="flex items-center gap-1.5">
          {nda && (
            <>
              <a href={`${API_BASE}/api/projects/${projectId}/broker-nda/file`} target="_blank" rel="noreferrer"
                title="NDA-PDF öffnen" data-testid="broker-nda-pdf-link"
                className="text-muted-foreground hover:text-sky-400 p-1"><FileDown className="w-4 h-4" /></a>
              <button onClick={rerun} disabled={busy} title="Erneut analysieren" data-testid="broker-nda-rerun-btn"
                className="text-muted-foreground hover:text-foreground p-1">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </button>
            </>
          )}
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="broker-nda-upload-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-8 px-3">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            {busy ? 'Analysiere…' : nda ? 'Neue Version hochladen' : 'NDA-PDF hochladen'}
          </Button>
        </div>
      </div>

      {!loading && versions.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2.5" data-testid="broker-nda-version-list">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-0.5">Versionen</span>
          {versions.map((v, i) => (
            <button key={v.version_id} onClick={() => !v.active && activateVersion(v.version_id)} disabled={busy}
              data-testid={`broker-nda-version-chip-${i}`}
              title={`${v.filename} · ${v.uploaded_by}${v.active ? ' (aktiv)' : ' — klicken zum Anzeigen & Prüfen'}`}
              className={`text-[10px] font-mono rounded-sm border px-2 py-1 flex items-center gap-1.5 transition-colors ${v.active
                ? 'border-sky-500/60 bg-sky-500/10 text-sky-300'
                : 'border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-sky-500/40'}`}>
              V{i + 1} · {fmtDt(v.uploaded_at)}
              {v.signed_at ? <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                : v.remarks_sent_at ? <Mail className="w-3 h-3 text-sky-400" />
                : v.review_status === 'done' && v.red_flags > 0
                  ? <span className="flex items-center gap-0.5 text-red-400"><AlertTriangle className="w-3 h-3" />{v.red_flags}</span>
                : v.review_status === 'running' ? <Loader2 className="w-3 h-3 animate-spin text-sky-400" /> : null}
            </button>
          ))}
        </div>
      )}

      {!loading && nda && mailNda && (
        <div className="flex items-center gap-2.5 border border-amber-500/30 bg-amber-950/20 rounded-sm p-2.5 flex-wrap mb-2.5"
          data-testid="broker-nda-new-mail-version">
          <Mail className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-xs text-foreground/85 flex-1 min-w-[200px]">
            NDA-Anhang in den E-Mails gefunden: <span className="font-medium text-amber-300">{mailNda.name}</span>
            {mailNda.from_name && <span className="text-muted-foreground"> · von {mailNda.from_name}</span>}
            {mailNda.received_at && <span className="text-muted-foreground"> · {fmtDt(mailNda.received_at)}</span>}
          </span>
          <Button size="sm" onClick={importFromMail} disabled={busy} data-testid="broker-nda-import-new-version-btn"
            className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-7 px-2.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
            Als neue Version übernehmen
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : !nda ? (
        <div className="space-y-2.5">
          <p className="text-xs text-muted-foreground">
            Der Broker hat ein NDA geschickt? PDF oder Word (.docx) hochladen — die KI prüft es gegen IFA/PRA,
            Kontaktverbote, Non-Circumvention & Co. und liefert fertige Änderungskommentare für die Antwort an den Broker.
          </p>
          {mailNda && (
            <div className="flex items-center gap-2.5 border border-sky-500/30 bg-sky-950/20 rounded-sm p-2.5 flex-wrap"
              data-testid="broker-nda-mail-hint">
              <Mail className="w-4 h-4 text-sky-400 shrink-0" />
              <span className="text-xs text-foreground/85 flex-1 min-w-[200px]">
                In den Broker-Mails gefunden: <span className="font-medium text-sky-300">{mailNda.name}</span>
                {mailNda.from_name && <span className="text-muted-foreground"> · von {mailNda.from_name}</span>}
              </span>
              <Button size="sm" onClick={importFromMail} disabled={busy} data-testid="broker-nda-import-mail-btn"
                className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-7 px-2.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
                Übernehmen & prüfen
              </Button>
            </div>
          )}
        </div>
      ) : nda.review_status === 'running' ? (
        <div className="space-y-2.5">
          <NdaStageStepper nda={nda} review={review} />
          <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="broker-nda-running">
            <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
            KI-Analyse von „{nda.filename}" läuft im Hintergrund — das Ergebnis erscheint hier automatisch.
          </p>
        </div>
      ) : nda.review_status === 'error' || !review ? (
        <p className="text-xs text-red-400 flex items-center gap-2" data-testid="broker-nda-error">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {nda.review_error || 'Analyse fehlgeschlagen'} — über den Kreis-Pfeil oben erneut starten.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {review?.recommendation === 'sign' ? (
              <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 flex items-center gap-1"
                data-testid="broker-nda-badge-sign">
                <CheckCircle2 className="w-3 h-3" /> Unterschriftsreif — keine Red Flags
              </span>
            ) : (
              <span className="text-[10px] font-mono border border-red-500/40 bg-red-500/10 text-red-300 rounded px-1.5 py-0.5 flex items-center gap-1"
                data-testid="broker-nda-badge-comment">
                <AlertTriangle className="w-3 h-3" /> {reds.length} Red Flag{reds.length !== 1 ? 's' : ''} — Änderungen empfohlen
              </span>
            )}
            {(review?.refs_used || []).length > 0 && (
              <span className="text-[10px] font-mono text-muted-foreground">geprüft gegen: {review.refs_used.join(' + ')}</span>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto">
              {versions.length > 1 && activeVersionIdx >= 0 ? `V${activeVersionIdx + 1} · ` : ''}{nda.filename} · {review?.analyzed_by} · {fmtDt(review?.analyzed_at)}
            </span>
          </div>

          <NdaStageStepper nda={nda} review={review} />

          {nda.signed_at ? (
            <div className="border border-emerald-500/30 bg-emerald-950/20 rounded-sm p-2.5 flex items-center gap-2 flex-wrap"
              data-testid="broker-nda-signed-banner">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-xs text-foreground/85 flex-1 min-w-[200px]">
                Gegengezeichnet von <b>{nda.signed_by}</b> · freigegeben von {nda.sign_approved_by} · gesendet an{' '}
                {(nda.sent_to || []).join(', ')} · {fmtDt(nda.signed_at)}
              </span>
              <a href={`${API_BASE}/api/projects/${projectId}/broker-nda/signed-file`} target="_blank" rel="noreferrer"
                className="text-[11px] text-emerald-300 underline shrink-0" data-testid="broker-nda-signed-file-link">
                Signiertes PDF
              </a>
            </div>
          ) : nda.sign_request ? (
            <div className="border border-amber-500/30 bg-amber-950/20 rounded-sm p-2.5 flex items-center gap-2 flex-wrap"
              data-testid="broker-nda-pending-banner">
              <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />
              <span className="text-xs text-foreground/85 flex-1 min-w-[200px]">
                Unterschrift wartet auf Vier-Augen-Freigabe — angefragt von <b>{nda.sign_request.requested_by}</b>{' '}
                ({fmtDt(nda.sign_request.requested_at)}). Freigabe unter „Pending Reviews".
              </span>
              {user?.id === nda.sign_request.requested_by_id && (
                <button onClick={cancelSign} data-testid="broker-nda-cancel-sign-btn"
                  className="text-[11px] text-muted-foreground hover:text-red-400 underline shrink-0">
                  Zurückziehen
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2.5 flex-wrap">
              <Button size="sm" onClick={openSignDialog} disabled={!isPdf} data-testid="broker-nda-sign-btn"
                title={!isPdf ? 'Nur PDF-NDAs können signiert werden — bitte die PDF-Version hochladen' : ''}
                className="bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-sm text-xs h-8">
                <PenLine className="w-3.5 h-3.5 mr-1.5" /> NDA unterschreiben (Vier-Augen)
              </Button>
              {!isPdf && <span className="text-[10px] text-muted-foreground">Nur PDF-NDAs können signiert werden.</span>}
              {nda.sign_rejection && (
                <span className="text-[11px] text-red-400" data-testid="broker-nda-rejection-hint">
                  Letzte Freigabe abgelehnt von {nda.sign_rejection.by}: „{nda.sign_rejection.reason}"
                </span>
              )}
            </div>
          )}

          {nda.remarks_sent_at && !nda.signed_at && (
            <div className="border border-sky-500/30 bg-sky-950/20 rounded-sm p-2.5 flex items-center gap-2 flex-wrap"
              data-testid="broker-nda-comments-sent-banner">
              <Mail className="w-4 h-4 text-sky-400 shrink-0" />
              <span className="text-xs text-foreground/85 flex-1 min-w-[200px]">
                Kommentare am {fmtDt(nda.remarks_sent_at)}{nda.remarks_sent_by ? <> von <b>{nda.remarks_sent_by}</b></> : null} an
                den Broker gesendet — kein erneutes Review nötig. Kommt die überarbeitete Fassung: „Neue Version hochladen".
              </span>
            </div>
          )}

          {reviewCollapsed && (
            <button onClick={() => setShowReview(v => !v)} data-testid="broker-nda-toggle-review"
              className="text-[11px] text-sky-300 hover:text-sky-200 flex items-center gap-1">
              {showReview ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showReview
                ? 'Prüfergebnis ausblenden'
                : `Prüfergebnis ansehen (${reds.length} Red Flag${reds.length !== 1 ? 's' : ''} · ${yellows.length} Yellow)`}
            </button>
          )}

          {detailsVisible && (<>
          {(review?.refs_missing || []).length > 0 && (
            <p className="text-[11px] text-amber-300/90 flex items-center gap-1.5" data-testid="broker-nda-refs-missing">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              {review.refs_missing.join(' & ')} nicht hinterlegt — nur allgemeine Prüfung. Upload: System-Seite → NDA-Referenzdokumente.
            </p>
          )}

          <p className="text-xs text-foreground/85" data-testid="broker-nda-summary">{review?.summary}</p>

          {reds.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-red-400 font-bold">Red Flags — auswählen, was in die E-Mail kommt</p>
                <button onClick={() => copyText(
                  selected.map((f, i) => `${i + 1}. ${f.title}${f.clause ? ` (${f.clause})` : ''}: ${f.comment || f.why}`).join('\n\n'),
                  `${selected.length} Kommentar(e) kopiert`)}
                  data-testid="broker-nda-copy-all-btn"
                  className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Auswahl kopieren
                </button>
              </div>
              {reds.map((f, i) => (
                <label key={i} className="flex items-start gap-2.5 border border-red-900/40 bg-red-950/20 rounded-sm p-2.5 cursor-pointer"
                  data-testid={`broker-nda-red-flag-${i}`}>
                  <Checkbox checked={!!checked[i]} onCheckedChange={v => setChecked(c => ({ ...c, [i]: !!v }))}
                    className="mt-0.5" data-testid={`broker-nda-red-flag-check-${i}`} />
                  <span className="text-xs flex-1">
                    <span className="text-red-300 font-medium">{f.title}</span>
                    {f.clause && <span className="text-muted-foreground italic"> · {f.clause}</span>}
                    <span className="block text-foreground/75 mt-0.5">{f.why}</span>
                    {f.comment && (
                      <span className="flex items-start gap-1.5 text-foreground/90 mt-1 border-l-2 border-red-500/40 pl-2">
                        <span className="flex-1">Kommentar-Vorschlag: {f.comment}</span>
                        <button onClick={(e) => { e.preventDefault(); copyText(f.comment, 'Kommentar kopiert'); }} title="Kommentar kopieren"
                          data-testid={`broker-nda-copy-comment-${i}`}
                          className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                          <Copy className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </span>
                </label>
              ))}
              <Button onClick={openMailDialog} disabled={selected.length === 0} size="sm" data-testid="broker-nda-email-btn"
                className="bg-red-600/80 hover:bg-red-500 text-white rounded-sm text-xs h-8">
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                {selected.length} Kommentar{selected.length !== 1 ? 'e' : ''} per E-Mail senden
              </Button>
            </div>
          )}

          {yellows.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Yellow Flags — nur zur Info</p>
              {yellows.map((f, i) => (
                <div key={i} className="flex items-start gap-2 border border-amber-900/30 bg-amber-950/10 rounded-sm p-2"
                  data-testid={`broker-nda-yellow-flag-${i}`}>
                  <Info className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <span className="text-xs">
                    <span className="text-amber-300">{f.title}</span>
                    {f.clause && <span className="text-muted-foreground italic"> · {f.clause}</span>}
                    <span className="block text-foreground/70 mt-0.5">{f.why}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          </>)}
        </div>
      )}

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-2xl bg-card border-border" data-testid="broker-nda-mail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-sky-400" /> NDA-Anmerkungen an den Broker senden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">An (Komma-getrennt)</p>
              <Input value={mail.to} onChange={e => setMail(m => ({ ...m, to: e.target.value }))}
                placeholder="broker@example.com" data-testid="broker-nda-mail-to"
                className="bg-secondary border-border rounded-sm text-xs h-8" />
              {recipients.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {recipients.map(r => (
                    <button key={r.email} onClick={() => setMail(m => ({ ...m, to: m.to ? `${m.to}, ${r.email}` : r.email }))}
                      data-testid={`broker-nda-recipient-chip-${r.email}`}
                      className="text-[10px] border border-border bg-secondary/40 hover:bg-secondary rounded-sm px-2 py-0.5 text-foreground/80">
                      + {r.name ? `${r.name} · ` : ''}{r.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">CC (optional)</p>
              <Input value={mail.cc} onChange={e => setMail(m => ({ ...m, cc: e.target.value }))}
                data-testid="broker-nda-mail-cc" className="bg-secondary border-border rounded-sm text-xs h-8" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Betreff</p>
              <Input value={mail.subject} onChange={e => setMail(m => ({ ...m, subject: e.target.value }))}
                data-testid="broker-nda-mail-subject" className="bg-secondary border-border rounded-sm text-xs h-8" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                Nachricht — vor dem Senden prüfen &amp; anpassen
              </p>
              <Textarea value={mail.body} onChange={e => setMail(m => ({ ...m, body: e.target.value }))}
                rows={14} data-testid="broker-nda-mail-body"
                className="bg-secondary border-border rounded-sm text-xs leading-relaxed" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] text-muted-foreground">Versand über dein verbundenes Microsoft-Postfach.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setMailOpen(false)} data-testid="broker-nda-mail-cancel"
                  className="rounded-sm text-xs h-8 border-border">Abbrechen</Button>
                <Button size="sm" onClick={sendMail} disabled={sending} data-testid="broker-nda-mail-send"
                  className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-8">
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                  {sending ? 'Sende…' : 'Senden'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="broker-nda-sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <PenLine className="w-4 h-4 text-emerald-400" /> NDA gegenzeichnen — Vier-Augen-Freigabe
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">
              Deine Unterschrift wird auf „{nda?.filename}" gestempelt. Vor dem Versand muss ein anderes
              Teammitglied die Anfrage in den Pending Reviews freigeben — wie in der Broker Deal Inbox.
            </p>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">An (Komma-getrennt)</p>
              <Input value={signMail.to} onChange={e => setSignMail(m => ({ ...m, to: e.target.value }))}
                placeholder="broker@example.com" data-testid="broker-nda-sign-to"
                className="bg-secondary border-border rounded-sm text-xs h-8" />
              {recipients.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {recipients.map(r => (
                    <button key={r.email} onClick={() => setSignMail(m => ({ ...m, to: m.to ? `${m.to}, ${r.email}` : r.email }))}
                      data-testid={`broker-nda-sign-recipient-${r.email}`}
                      className="text-[10px] border border-border bg-secondary/40 hover:bg-secondary rounded-sm px-2 py-0.5 text-foreground/80">
                      + {r.name ? `${r.name} · ` : ''}{r.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Unterschrift</p>
                <button onClick={() => setSigMode('draw')} data-testid="broker-nda-sig-mode-draw"
                  className={`text-[10px] px-2 py-0.5 rounded-sm border ${sigMode === 'draw' ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-300' : 'border-border text-muted-foreground'}`}>
                  Zeichnen
                </button>
                <button onClick={() => setSigMode('type')} data-testid="broker-nda-sig-mode-type"
                  className={`text-[10px] px-2 py-0.5 rounded-sm border ${sigMode === 'type' ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-300' : 'border-border text-muted-foreground'}`}>
                  Name tippen
                </button>
              </div>
              {sigMode === 'draw' ? (
                <SignaturePad onChange={setSigData} />
              ) : null}
              <div className="mt-1.5">
                <p className="text-[10px] text-muted-foreground mb-1">Name des Unterzeichners</p>
                <Input value={sigName} onChange={e => setSigName(e.target.value)} data-testid="broker-nda-sign-name"
                  className="bg-secondary border-border rounded-sm text-xs h-8" />
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">E-Mail-Text an den Broker</p>
              <Textarea value={signMail.body} onChange={e => setSignMail(m => ({ ...m, body: e.target.value }))}
                rows={6} data-testid="broker-nda-sign-body"
                className="bg-secondary border-border rounded-sm text-xs leading-relaxed" />
              <p className="text-[10px] text-muted-foreground mt-1">Versand nach Freigabe über das Postfach des Anfragenden — das signierte PDF wird automatisch angehängt.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setSignOpen(false)} data-testid="broker-nda-sign-cancel"
                className="rounded-sm text-xs h-8 border-border">Abbrechen</Button>
              <Button size="sm" onClick={requestSign} disabled={signSending} data-testid="broker-nda-sign-submit"
                className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-8">
                {signSending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5 mr-1.5" />}
                Freigabe anfragen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
